import { useParams, useNavigate } from "react-router-dom"
import { LocationForm } from "./location-form"
import { useQuery } from "@apollo/client"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { GraphqlError } from "~/ui/errors"
import { useToast } from "~/ui/use-toast"
import { LocationStatus } from "~/__generated__/graphql"

const locationQuery = gql(/* GraphQL */ `
  query Location($id: ID!) {
    node(id: $id) {
      ... on Location {
        id
        name
        status
        websiteUrl
        googleMapsUrl
        latitude
        longitude
        streetAddress
        streetAddress2
        city
        stateProvince
        postalCode
        country
        formattedAddress
        placeId
        administrativeAreaLevel1
        administrativeAreaLevel2
        neighborhood
        phone
        timeZone
      }
    }
  }
`)

const updateLocationMutation = gql(/* GraphQL */ `
  mutation UpdateLocation(
    $id: ID!
    $name: String
    $status: LocationStatus
    $websiteUrl: String
    $googleMapsUrl: String
    $latitude: Float
    $longitude: Float
    $streetAddress: String
    $streetAddress2: String
    $city: String
    $stateProvince: String
    $postalCode: String
    $country: String
    $formattedAddress: String
    $placeId: String
    $administrativeAreaLevel1: String
    $administrativeAreaLevel2: String
    $neighborhood: String
    $phone: String
    $timeZone: String
  ) {
    locationUpdate(
      input: {
        id: $id
        name: $name
        status: $status
        websiteUrl: $websiteUrl
        googleMapsUrl: $googleMapsUrl
        latitude: $latitude
        longitude: $longitude
        streetAddress: $streetAddress
        streetAddress2: $streetAddress2
        city: $city
        stateProvince: $stateProvince
        postalCode: $postalCode
        country: $country
        formattedAddress: $formattedAddress
        placeId: $placeId
        administrativeAreaLevel1: $administrativeAreaLevel1
        administrativeAreaLevel2: $administrativeAreaLevel2
        neighborhood: $neighborhood
        phone: $phone
        timeZone: $timeZone
      }
    ) {
      location {
        id
        name
        status
        websiteUrl
        googleMapsUrl
        latitude
        longitude
        streetAddress
        streetAddress2
        city
        stateProvince
        postalCode
        country
        formattedAddress
        placeId
        administrativeAreaLevel1
        administrativeAreaLevel2
        neighborhood
        phone
        timeZone
      }
    }
  }
`)

export const LocationEditScreen = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data, error } = useQuery(locationQuery, {
    variables: { id: id! },
  })
  const [updateLocation] = useSafeMutation(updateLocationMutation)

  if (error) {
    return <GraphqlError error={error} />
  }

  const location = data?.node
  if (!location || location.__typename !== "Location") {
    return <div>Location not found</div>
  }

  return (
    <TablePageLayout>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Edit Location</h1>
      </div>
      <hr className="my-4 border-0 border-b" />
      <LocationForm
        defaultValues={{
          name: location.name,
          status: location.status,
          websiteUrl: location.websiteUrl || undefined,
          googleMapsUrl: location.googleMapsUrl || undefined,
          latitude: location.latitude || undefined,
          longitude: location.longitude || undefined,
          streetAddress: location.streetAddress || undefined,
          streetAddress2: location.streetAddress2 || undefined,
          city: location.city || undefined,
          stateProvince: location.stateProvince || undefined,
          postalCode: location.postalCode || undefined,
          country: location.country || undefined,
          formattedAddress: location.formattedAddress || undefined,
          placeId: location.placeId || undefined,
          administrativeAreaLevel1: location.administrativeAreaLevel1 || undefined,
          administrativeAreaLevel2: location.administrativeAreaLevel2 || undefined,
          neighborhood: location.neighborhood || undefined,
          phone: location.phone || undefined,
          timeZone: location.timeZone || undefined,
        }}
        onSubmit={async (formData) => {
          const { data: result, errors } = await updateLocation({
            variables: {
              id: location.id,
              name: formData.name,
              status: formData.status as LocationStatus,
              websiteUrl: formData.websiteUrl,
              googleMapsUrl: formData.googleMapsUrl,
              latitude: formData.latitude,
              longitude: formData.longitude,
              streetAddress: formData.streetAddress,
              streetAddress2: formData.streetAddress2,
              city: formData.city,
              stateProvince: formData.stateProvince,
              postalCode: formData.postalCode,
              country: formData.country,
              formattedAddress: formData.formattedAddress,
              placeId: formData.placeId,
              administrativeAreaLevel1: formData.administrativeAreaLevel1,
              administrativeAreaLevel2: formData.administrativeAreaLevel2,
              neighborhood: formData.neighborhood,
              phone: formData.phone,
              timeZone: formData.timeZone,
            },
          })

          if (result?.locationUpdate?.location && !errors) {
            toast({
              title: "Success",
              description: "Location updated successfully",
            })
            navigate("/locations")
          }
        }}
        mode="edit"
      />
    </TablePageLayout>
  )
}
